import { existsSync, readFileSync, statSync } from "node:fs";
import { homedir } from "node:os";
import path from "node:path";

export type ResolvedMode =
  | { mode: "local"; dbPath: string }
  | { mode: "remote"; url: string; token?: string };

export interface ResolveOpts {
  forceLocal?: boolean;
  forceRemote?: boolean;
  projectDir?: string;
  platformUrl?: string;
}

const MAX_WALK_LEVELS = 6;

/**
 * Decide whether to read from a local SQLite log DB or call the platform via
 * tRPC.
 *
 * Resolution rules (in order):
 * - `--local` and `--remote` together → throws.
 * - `--local` → search the project tree for `.skaile/logs.db`. Throws if not found.
 * - `--remote` → require a platform URL (flag, env, or settings).
 * - Auto: prefer local if a `.skaile/logs.db` is found in the workspace tree;
 *   otherwise fall back to remote if a URL is configured. Throws otherwise.
 */
export function resolveMode(opts: ResolveOpts): ResolvedMode {
  if (opts.forceLocal && opts.forceRemote) {
    throw new Error("Cannot use both --local and --remote");
  }

  const startDir = path.resolve(opts.projectDir ?? process.cwd());
  const localDb = findLogsDb(startDir);

  if (opts.forceLocal) {
    if (!localDb) {
      throw new Error(
        `Local mode requested but no .skaile/logs.db found within ${MAX_WALK_LEVELS} levels of ${startDir}`,
      );
    }
    return { mode: "local", dbPath: localDb };
  }

  const remoteUrl = opts.platformUrl ?? readPlatformUrlFromEnvOrSettings();
  const token = readApiToken();

  if (opts.forceRemote) {
    if (!remoteUrl) {
      throw new Error(
        `Remote mode requested but no platform URL configured. Use --platform-url, set SKAILE_PLATFORM_URL, or add "platformUrl" to ~/.skaile/settings.json.`,
      );
    }
    return { mode: "remote", url: remoteUrl, token };
  }

  // Auto-detect.
  if (localDb) {
    return { mode: "local", dbPath: localDb };
  }
  if (remoteUrl) {
    return { mode: "remote", url: remoteUrl, token };
  }
  throw new Error(
    `No local .skaile/logs.db found in ${startDir} and no platform URL configured. Use --project-dir to point at a workspace, or configure --platform-url / SKAILE_PLATFORM_URL.`,
  );
}

function findLogsDb(startDir: string): string | undefined {
  let dir = startDir;
  for (let i = 0; i < MAX_WALK_LEVELS; i++) {
    const candidate = path.join(dir, ".skaile", "logs.db");
    if (existsSync(candidate)) {
      try {
        if (statSync(candidate).isFile()) return candidate;
      } catch {
        // ignore
      }
    }
    const parent = path.dirname(dir);
    if (parent === dir) return undefined;
    dir = parent;
  }
  return undefined;
}

function readPlatformUrlFromEnvOrSettings(): string | undefined {
  const envUrl = process.env.SKAILE_PLATFORM_URL;
  if (envUrl) return envUrl;
  const settings = readGlobalSettings();
  if (typeof settings.platformUrl === "string" && settings.platformUrl) {
    return settings.platformUrl;
  }
  return undefined;
}

function readApiToken(): string | undefined {
  const envToken = process.env.SKAILE_API_TOKEN;
  if (envToken) return envToken;
  const settings = readGlobalSettings();
  if (typeof settings.apiToken === "string" && settings.apiToken) {
    return settings.apiToken;
  }
  return undefined;
}

function readGlobalSettings(): Record<string, unknown> {
  try {
    const settingsPath = path.join(homedir(), ".skaile", "settings.json");
    if (!existsSync(settingsPath)) return {};
    return JSON.parse(readFileSync(settingsPath, "utf8")) as Record<string, unknown>;
  } catch {
    return {};
  }
}
